import config from "../config/config";
import { getSignupSource } from "./signupSource";

/*
 * THE SIGNUP JOURNEY, from the browser (2026-10-22; docs/SIGNUP_JOURNEY_DESIGN.md): reports each step a person reaches while signing up, to a public endpoint, so a person who stops before the
 * account exists still leaves a record (with their address only from step 1, which is when they pressed Next with it). The server cleans and whitelists everything (UserApi/Helper/signupJourney.ts).
 *
 * It can NEVER get in the way of signing up: nothing awaits it, every failure is swallowed, storage that is blocked just means a fresh id. A password is never sent. It does nothing in the demo build
 * (which talks to nothing) and in a build where REACT_APP_SIGNUP_JOURNEY is not "1" (staging sets it; production does not until the backend is promoted).
 */
export type JourneyStage = "clicked" | "opened" | "step1" | "step2" | "step3" | "step4" | "verified";

export const ATTEMPT_KEY = "skydive.signupAttempt";
export const ATTEMPT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const ATTEMPT_ID = /^[a-f0-9]{32}$/;

export const newAttemptId = (): string => {
  const bytes = new Uint8Array(16);
  const c = (window as any).crypto || (window as any).msCrypto;
  // every real browser has crypto; the fallback only keeps an old or odd one from breaking the signup (an id names a record, it is not a secret)
  if (c && c.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);

  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * The id of THIS person's attempt: the one in the address (`?sa=`, put on the signup link by the marketing site so its click and these steps are one record), else the one this browser already
 * has (a refresh or a return continues the same attempt, for up to 30 days), else a new one.
 */
export const getAttemptId = (search: string = window.location.search, now: number = Date.now()): string => {
  let fromAddress: string | null = null;
  try {
    const v = new URLSearchParams(search).get("sa");
    if (v && ATTEMPT_ID.test(v)) fromAddress = v;
  } catch { /* an unparseable address has no id */ }
  try {
    const stored = JSON.parse(window.localStorage.getItem(ATTEMPT_KEY) || "null");
    const fresh = stored && ATTEMPT_ID.test(stored.id) && now - Number(stored.at) < ATTEMPT_MAX_AGE_MS;
    const id = fromAddress || (fresh ? stored.id : newAttemptId());
    window.localStorage.setItem(ATTEMPT_KEY, JSON.stringify({ id, at: fresh && id === stored.id ? stored.at : now }));

    return id;
  } catch {
    return fromAddress || newAttemptId();
  }
};

/** The Skydive cookie's token (it names the visitor's session on the marketing site), or undefined. Never anything but that cookie. */
export const skydiveToken = (cookie: string = document.cookie): string | undefined => {
  const m = /(?:^|;\s*)skydive=([^;]*)/.exec(cookie);
  if (!m) return undefined;
  const token = m[1].split("url")[0];

  return token && token.length <= 200 ? token : undefined;
};

export interface JourneyExtra { email?: string; firstName?: string; quiz?: { role?: string; siteType?: string; monthlyUsers?: string }; siteUrl?: string }

const enabled = (): boolean => process.env.REACT_APP_DEMO !== "1" && process.env.REACT_APP_SIGNUP_JOURNEY === "1";

/** Tells the server this person reached `stage`. Fire and forget. */
export const reportStage = (stage: JourneyStage, extra: JourneyExtra = {}): void => {
  try {
    if (!enabled()) return;
    const body = JSON.stringify({ attemptId: getAttemptId(), stage, source: getSignupSource(), skydive: skydiveToken(), ...extra });
    fetch(`${config.baseUrl}user/signupJourney`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true, credentials: "omit" }).catch(() => undefined);
  } catch { /* signing up must never depend on this */ }
};
